// src/hooks/useScene.ts
/**
 * Fixed useScene hook - handles empty currentViewId
 * 
 * Issue: currentViewId starts as empty string "", causing error in getItemByIdOrThrow
 * Fix: Return early with safe defaults when currentViewId is empty
 */

import { useCallback, useMemo } from 'react';
import {
  ModelItem,
  ViewItem,
  Connector,
  TextBox,
  Rectangle,
  ItemReference,
  LayerOrderingAction
} from 'src/types';
import { useUiStateStore } from 'src/stores/uiStateStore';
import { useModelStore } from 'src/stores/modelStore';
import { useSceneStore } from 'src/stores/sceneStore';
import * as reducers from 'src/stores/reducers';
import type { State } from 'src/stores/reducers/types';
import { getItemByIdOrThrow } from 'src/utils';
import {
  CONNECTOR_DEFAULTS,
  RECTANGLE_DEFAULTS,
  TEXTBOX_DEFAULTS
} from 'src/config';

export const useScene = () => {
  const model = useModelStore((state) => {
    return state;
  });

  const scene = useSceneStore((state) => {
    return state;
  });

  const currentViewId = useUiStateStore((state) => {
    return state.view;
  });

  // FIX: Handle empty currentViewId (before initialization)
  const currentView = useMemo(() => {
    // If no view ID yet (empty string), return first view or null
    if (!currentViewId || currentViewId === '') {
      if (model.views && model.views.length > 0) {
        return model.views[0];
      }
      // Return empty view structure as fallback
      return {
        id: '',
        name: '',
        items: [],
        connectors: [],
        rectangles: [],
        textBoxes: []
      };
    }

    try {
      return getItemByIdOrThrow(model.views, currentViewId).value;
    } catch (error) {
      console.warn(`View not found: ${currentViewId}, using first view`);
      // Fallback to first view if current ID is invalid
      if (model.views && model.views.length > 0) {
        return model.views[0];
      }
      // Return empty view structure as last resort
      return {
        id: '',
        name: '',
        items: [],
        connectors: [],
        rectangles: [],
        textBoxes: []
      };
    }
  }, [currentViewId, model.views]);

  const items = useMemo(() => {
    return currentView?.items ?? [];
  }, [currentView]);

  const colors = useMemo(() => {
    return model.colors ?? [];
  }, [model.colors]);

  const connectors = useMemo(() => {
    return (currentView?.connectors ?? []).map((connector) => {
      const sceneConnector = scene.connectors[connector.id];

      return {
        ...CONNECTOR_DEFAULTS,
        ...connector,
        ...sceneConnector
      };
    });
  }, [currentView, scene.connectors]);

  const rectangles = useMemo(() => {
    return (currentView?.rectangles ?? []).map((rectangle) => {
      return {
        ...RECTANGLE_DEFAULTS,
        ...rectangle
      };
    });
  }, [currentView]);

  const textBoxes = useMemo(() => {
    return (currentView?.textBoxes ?? []).map((textBox) => {
      const sceneTextBox = scene.textBoxes[textBox.id];

      return {
        ...TEXTBOX_DEFAULTS,
        ...textBox,
        ...sceneTextBox
      };
    });
  }, [currentView, scene.textBoxes]);

  const getState = useCallback(() => {
    return {
      model: model.actions.get(),
      scene: scene.actions.get()
    };
  }, [model.actions, scene.actions]);

  const setState = useCallback(
    (newState: State) => {
      model.actions.set(newState.model);
      scene.actions.set(newState.scene);
    },
    [model.actions, scene.actions]
  );

  const createModelItem = useCallback(
    (newModelItem: ModelItem) => {
      const newState = reducers.createModelItem(newModelItem, getState());
      setState(newState);
    },
    [getState, setState]
  );

  const updateModelItem = useCallback(
    (id: string, updates: Partial<ModelItem>) => {
      const newState = reducers.updateModelItem(id, updates, getState());
      setState(newState);
    },
    [getState, setState]
  );

  const deleteModelItem = useCallback(
    (id: string) => {
      const newState = reducers.deleteModelItem(id, getState());
      setState(newState);
    },
    [getState, setState]
  );

  const createViewItem = useCallback(
    (newViewItem: ViewItem) => {
      // Guard against empty currentViewId
      if (!currentViewId || currentViewId === '') {
        console.warn('Cannot create view item: no view selected');
        return;
      }

      const newState = reducers.view({
        action: 'CREATE_VIEWITEM',
        payload: newViewItem,
        ctx: { viewId: currentViewId, state: getState() }
      });
      setState(newState);
    },
    [getState, setState, currentViewId]
  );

  const updateViewItem = useCallback(
    (id: string, updates: Partial<ViewItem>) => {
      if (!currentViewId || currentViewId === '') {
        console.warn('Cannot update view item: no view selected');
        return;
      }

      const newState = reducers.view({
        action: 'UPDATE_VIEWITEM',
        payload: { id, ...updates },
        ctx: { viewId: currentViewId, state: getState() }
      });
      setState(newState);
    },
    [getState, setState, currentViewId]
  );

  const deleteViewItem = useCallback(
    (id: string) => {
      if (!currentViewId || currentViewId === '') {
        console.warn('Cannot delete view item: no view selected');
        return;
      }

      const newState = reducers.view({
        action: 'DELETE_VIEWITEM',
        payload: id,
        ctx: { viewId: currentViewId, state: getState() }
      });
      setState(newState);
    },
    [getState, setState, currentViewId]
  );

  const createConnector = useCallback(
    (newConnector: Connector) => {
      if (!currentViewId || currentViewId === '') {
        console.warn('Cannot create connector: no view selected');
        return;
      }

      const newState = reducers.view({
        action: 'CREATE_CONNECTOR',
        payload: newConnector,
        ctx: { viewId: currentViewId, state: getState() }
      });
      setState(newState);
    },
    [getState, setState, currentViewId]
  );

  const updateConnector = useCallback(
    (id: string, updates: Partial<Connector>) => {
      if (!currentViewId || currentViewId === '') {
        console.warn('Cannot update connector: no view selected');
        return;
      }

      const newState = reducers.view({
        action: 'UPDATE_CONNECTOR',
        payload: { id, ...updates },
        ctx: { viewId: currentViewId, state: getState() }
      });
      setState(newState);
    },
    [getState, setState, currentViewId]
  );

  const deleteConnector = useCallback(
    (id: string) => {
      if (!currentViewId || currentViewId === '') {
        console.warn('Cannot delete connector: no view selected');
        return;
      }

      const newState = reducers.view({
        action: 'DELETE_CONNECTOR',
        payload: id,
        ctx: { viewId: currentViewId, state: getState() }
      });
      setState(newState);
    },
    [getState, setState, currentViewId]
  );

  const createTextBox = useCallback(
    (newTextBox: TextBox) => {
      if (!currentViewId || currentViewId === '') {
        console.warn('Cannot create text box: no view selected');
        return;
      }

      const newState = reducers.view({
        action: 'CREATE_TEXTBOX',
        payload: newTextBox,
        ctx: { viewId: currentViewId, state: getState() }
      });
      setState(newState);
    },
    [getState, setState, currentViewId]
  );

  const updateTextBox = useCallback(
    (id: string, updates: Partial<TextBox>) => {
      if (!currentViewId || currentViewId === '') {
        console.warn('Cannot update text box: no view selected');
        return;
      }

      const newState = reducers.view({
        action: 'UPDATE_TEXTBOX',
        payload: { id, ...updates },
        ctx: { viewId: currentViewId, state: getState() }
      });
      setState(newState);
    },
    [getState, setState, currentViewId]
  );

  const deleteTextBox = useCallback(
    (id: string) => {
      if (!currentViewId || currentViewId === '') {
        console.warn('Cannot delete text box: no view selected');
        return;
      }

      const newState = reducers.view({
        action: 'DELETE_TEXTBOX',
        payload: id,
        ctx: { viewId: currentViewId, state: getState() }
      });
      setState(newState);
    },
    [getState, setState, currentViewId]
  );

  const createRectangle = useCallback(
    (newRectangle: Rectangle) => {
      if (!currentViewId || currentViewId === '') {
        console.warn('Cannot create rectangle: no view selected');
        return;
      }

      const newState = reducers.view({
        action: 'CREATE_RECTANGLE',
        payload: newRectangle,
        ctx: { viewId: currentViewId, state: getState() }
      });
      setState(newState);
    },
    [getState, setState, currentViewId]
  );

  const updateRectangle = useCallback(
    (id: string, updates: Partial<Rectangle>) => {
      if (!currentViewId || currentViewId === '') {
        console.warn('Cannot update rectangle: no view selected');
        return;
      }

      const newState = reducers.view({
        action: 'UPDATE_RECTANGLE',
        payload: { id, ...updates },
        ctx: { viewId: currentViewId, state: getState() }
      });
      setState(newState);
    },
    [getState, setState, currentViewId]
  );

  const deleteRectangle = useCallback(
    (id: string) => {
      if (!currentViewId || currentViewId === '') {
        console.warn('Cannot delete rectangle: no view selected');
        return;
      }

      const newState = reducers.view({
        action: 'DELETE_RECTANGLE',
        payload: id,
        ctx: { viewId: currentViewId, state: getState() }
      });
      setState(newState);
    },
    [getState, setState, currentViewId]
  );

  const changeLayerOrder = useCallback(
    (action: LayerOrderingAction, item: ItemReference) => {
      if (!currentViewId || currentViewId === '') {
        console.warn('Cannot change layer order: no view selected');
        return;
      }

      const newState = reducers.view({
        action: 'CHANGE_LAYER_ORDER',
        payload: { action, item },
        ctx: { viewId: currentViewId, state: getState() }
      });
      setState(newState);
    },
    [getState, setState, currentViewId]
  );

  const toggleLayerVisibility = useCallback(
    (id: string, type: import('src/features/layers').LayerType) => {
      if (!currentViewId || currentViewId === '') {
        console.warn('Cannot toggle layer visibility: no view selected');
        return;
      }

      const newState = reducers.view({
        action: 'TOGGLE_LAYER_VISIBILITY',
        payload: { id, type },
        ctx: { viewId: currentViewId, state: getState() }
      });
      setState(newState);
    },
    [getState, setState, currentViewId]
  );

  const setLayerVisibility = useCallback(
    (id: string, type: import('src/features/layers').LayerType, visible: boolean) => {
      if (!currentViewId || currentViewId === '') {
        console.warn('Cannot set layer visibility: no view selected');
        return;
      }

      const newState = reducers.view({
        action: 'SET_LAYER_VISIBILITY',
        payload: { id, type, visible },
        ctx: { viewId: currentViewId, state: getState() }
      });
      setState(newState);
    },
    [getState, setState, currentViewId]
  );

  const toggleLayerLocked = useCallback(
    (id: string, type: import('src/features/layers').LayerType) => {
      if (!currentViewId || currentViewId === '') {
        console.warn('Cannot toggle layer locked: no view selected');
        return;
      }

      const newState = reducers.view({
        action: 'TOGGLE_LAYER_LOCKED',
        payload: { id, type },
        ctx: { viewId: currentViewId, state: getState() }
      });
      setState(newState);
    },
    [getState, setState, currentViewId]
  );

  const setLayerLocked = useCallback(
    (id: string, type: import('src/features/layers').LayerType, locked: boolean) => {
      if (!currentViewId || currentViewId === '') {
        console.warn('Cannot set layer locked: no view selected');
        return;
      }

      const newState = reducers.view({
        action: 'SET_LAYER_LOCKED',
        payload: { id, type, locked },
        ctx: { viewId: currentViewId, state: getState() }
      });
      setState(newState);
    },
    [getState, setState, currentViewId]
  );

  const showAllLayers = useCallback(() => {
    if (!currentViewId || currentViewId === '') {
      console.warn('Cannot show all layers: no view selected');
      return;
    }

    const newState = reducers.view({
      action: 'SHOW_ALL_LAYERS',
      payload: undefined,
      ctx: { viewId: currentViewId, state: getState() }
    });
    setState(newState);
  }, [getState, setState, currentViewId]);

  const hideAllLayers = useCallback(() => {
    if (!currentViewId || currentViewId === '') {
      console.warn('Cannot hide all layers: no view selected');
      return;
    }

    const newState = reducers.view({
      action: 'HIDE_ALL_LAYERS',
      payload: undefined,
      ctx: { viewId: currentViewId, state: getState() }
    });
    setState(newState);
  }, [getState, setState, currentViewId]);

  const lockAllLayers = useCallback(() => {
    if (!currentViewId || currentViewId === '') {
      console.warn('Cannot lock all layers: no view selected');
      return;
    }

    const newState = reducers.view({
      action: 'LOCK_ALL_LAYERS',
      payload: undefined,
      ctx: { viewId: currentViewId, state: getState() }
    });
    setState(newState);
  }, [getState, setState, currentViewId]);

  const unlockAllLayers = useCallback(() => {
    if (!currentViewId || currentViewId === '') {
      console.warn('Cannot unlock all layers: no view selected');
      return;
    }

    const newState = reducers.view({
      action: 'UNLOCK_ALL_LAYERS',
      payload: undefined,
      ctx: { viewId: currentViewId, state: getState() }
    });
    setState(newState);
  }, [getState, setState, currentViewId]);

  return {
    items,
    connectors,
    colors,
    rectangles,
    textBoxes,
    currentView,
    createModelItem,
    updateModelItem,
    deleteModelItem,
    createViewItem,
    updateViewItem,
    deleteViewItem,
    createConnector,
    updateConnector,
    deleteConnector,
    createTextBox,
    updateTextBox,
    deleteTextBox,
    createRectangle,
    updateRectangle,
    deleteRectangle,
    changeLayerOrder,
    toggleLayerVisibility,
    setLayerVisibility,
    toggleLayerLocked,
    setLayerLocked,
    showAllLayers,
    hideAllLayers,
    lockAllLayers,
    unlockAllLayers
  };
};